import { useEffect, useState } from 'react';

export type UseDebounceOptions = {
  delay: number;
  enabled?: boolean;
};

/**
 * Delays updating the value until after a specified delay has passed since the last change.
 * If `enabled` is false, the value updates immediately without debouncing.
 * @returns The debounced value.
 */
export const useDebounce = <T>(
  value: T,
  { delay, enabled = true }: UseDebounceOptions,
): T => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    if (!enabled) {
      setDebouncedValue(value);
      return;
    }
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay, enabled]);

  return debouncedValue;
};
